import { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  IconButton,
  Stack,
  useTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { capitalize } from "../../config/settings.config";

const MotionCard = motion.create ? motion.create(Card) : motion(Card);
const MotionBox = motion.create ? motion.create(Box) : motion(Box);

export default function LeadTimeline({ timeline = [], statusList = [] }) {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const getStatus = (key) => statusList.find((s) => s.key === key) || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Chronological order (oldest first → latest last, i.e. 1 → 2 → 3)
  const sorted = [...timeline];
  const hasMiddle = sorted.length > 2;

  const getStatusIcon = (index) => {
    if (index === 0) return <TrendingUpIcon sx={{ fontSize: 18 }} />;
    return <PriorityHighIcon sx={{ fontSize: 18 }} />;
  };

  const renderItem = (t, i, { isLast = false, hideConnector = false, mb = 1.5 } = {}) => {
    const st = getStatus(t.key);
    return (
      <MotionBox
        key={`${t.key}-${t.date}-${i}`}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.3, delay: i * 0.08 }}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: isLast ? 0 : mb,
          position: "relative",
        }}
      >
        {/* Timeline dot and line */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: st.color ? `${st.color}15` : "rgba(124, 58, 237, 0.1)",
              color: st.color || theme.palette.primary.main,
            }}
          >
            {st.icon || getStatusIcon(i)}
          </Avatar>
          {!isLast && !hideConnector && (
            <Box
              sx={{
                width: 2,
                height: 16,
                bgcolor: "#E2E8F0",
                mt: 0.5,
              }}
            />
          )}
        </Box>

        {/* Content */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ flex: 1, pt: 0.5 }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 0.75,
              borderRadius: 5,
              bgcolor: st.color || theme.palette.primary.main,
              color: "#FFFFFF",
              fontSize: 13,
              fontWeight: 600,
              textTransform: "capitalize",
              minWidth: 100,
              textAlign: "center",
            }}
          >
            {capitalize(st.label || st.key || t.key)}
          </Box>

          <Typography
            sx={{
              fontSize: 12,
              color: "#94A3B8",
              whiteSpace: "nowrap",
              ml: 2,
            }}
          >
            {formatDate(t.date)}
          </Typography>
        </Stack>
      </MotionBox>
    );
  };

  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      sx={{
        borderRadius: 4,
        bgcolor: "#FFFFFF",
        boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        border: "1px solid #F1F5F9",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 3,
          }}
        >
          Status Timeline
        </Typography>

        <Box sx={{ position: "relative" }}>
          {/* First item (oldest status) */}
          {sorted.length > 0 &&
            renderItem(sorted[0], 0, {
              isLast: sorted.length === 1,
              hideConnector: hasMiddle && !expanded,
              mb: hasMiddle && !expanded ? 0 : 1.5,
            })}

          {/* Expand button — centered between first and last */}
          {hasMiddle && !expanded && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                my: 1.54,
              }}
            >
              <MotionBox
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <IconButton
                  onClick={() => setExpanded(true)}
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: "#F1F5F9",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "#E2E8F0",
                    },
                  }}
                >
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 18,
                      color: "#64748B",
                    }}
                  />
                </IconButton>
              </MotionBox>
            </Box>
          )}

          {/* Middle items (only when expanded) */}
          <AnimatePresence>
            {hasMiddle &&
              expanded &&
              sorted.slice(1, -1).map((t, i) =>
                renderItem(t, i + 1),
              )}
          </AnimatePresence>

          {/* Collapse button — centered */}
          {hasMiddle && expanded && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                my: 1.54,
              }}
            >
              <MotionBox
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <IconButton
                  onClick={() => setExpanded(false)}
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: "#F1F5F9",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "#E2E8F0",
                    },
                  }}
                >
                  <KeyboardArrowDownIcon
                    sx={{
                      fontSize: 18,
                      color: "#64748B",
                      transform: "rotate(180deg)",
                      transition: "transform 0.3s ease",
                    }}
                  />
                </IconButton>
              </MotionBox>
            </Box>
          )}

          {/* Last item (latest status) */}
          {sorted.length > 1 &&
            renderItem(sorted[sorted.length - 1], sorted.length - 1, {
              isLast: true,
            })}
        </Box>
      </CardContent>
    </MotionCard>
  );
}
